import { APP_PLATFORM_TYPES, AppNavigationItem } from './navigation.types';

export const mainNavigation: AppNavigationItem[] = [

    // ─── Home ────────────────────────────────────────────────────────────────
    // Visible to all tenant staff. super_admin only manages the platform and
    // has no operational home page, so it is excluded here.
    {
        id   : 'home',
        title: 'Home',
        type : 'basic',
        icon : 'heroicons_outline:home',
        link : '/dashboard',
        meta : {
            platform   : APP_PLATFORM_TYPES.Both,
            roles      : ['system_admin', 'office_staff', 'field_staff'],
            hiddenInApp: true,
        },
    },

    // ─── User Guide ──────────────────────────────────────────────────────────
    // Visible to every authenticated role, including super_admin — unlike
    // `home`, which deliberately excludes it. List all four roles explicitly
    // rather than omitting `roles` (no item in this codebase relies on
    // "omit roles = everyone").
    {
        id   : 'user-guide',
        title: 'User Guide',
        type : 'basic',
        icon : 'heroicons_outline:book-open',
        link : '/user-guide',
        meta : {
            platform   : APP_PLATFORM_TYPES.Both,
            roles      : ['system_admin', 'super_admin', 'office_staff', 'field_staff'],
            hiddenInApp: true,
        },
    },

    // ─── Requests ────────────────────────────────────────────────────────────
    // Day-to-day operational flows. Not relevant for super_admin.
    {
        id      : 'requests',
        title   : 'Requests',
        subtitle: 'Client forms, signatures & office tasks',
        type    : 'group',
        meta    : {
            platform: APP_PLATFORM_TYPES.Both,
            roles   : ['system_admin', 'office_staff', 'field_staff', 'support'],
        },
        children: [
            {
                id   : 'requests.my',
                title: 'My requests',
                type : 'basic',
                icon : 'mat_outline:note_alt',
                link : '/requests/my-requests',
                // Restricted at the child level, not just the group — support
                // sees the "Requests" group (for Office queue) but cannot
                // create requests, so this item must not inherit the group's
                // broader role set.
                meta : {
                    platform: APP_PLATFORM_TYPES.Both,
                    roles   : ['system_admin', 'office_staff', 'field_staff'],
                },
            },
            {
                id   : 'requests.office_queue',
                title: 'Office queue',
                type : 'basic',
                icon : 'mat_outline:assignment_turned_in',
                link : '/requests/office-queue',
                meta : { platform: APP_PLATFORM_TYPES.Web, requiresOfficeAccess: true, hiddenInApp: true },
            },
        ],
    },

    // ─── Admin ───────────────────────────────────────────────────────────────
    // User management and internal settings. system_admin and support — support
    // has full system_admin parity except it can never create or act on
    // requests (see app-roles.interface.ts).
    {
        id      : 'admin',
        title   : 'Admin',
        subtitle: 'Manage users and settings',
        type    : 'group',
        meta    : {
            platform   : APP_PLATFORM_TYPES.Web,
            roles      : ['system_admin', 'support'],
            hiddenInApp: true,
        },
        children: [
            {
                id   : 'admin.users',
                title: 'Users',
                type : 'basic',
                icon : 'mat_outline:manage_accounts',
                link : '/admin/users/list',
                meta : { platform: APP_PLATFORM_TYPES.Web },
            },
            {
                id   : 'admin.templates',
                title: 'Request templates',
                type : 'basic',
                icon : 'mat_outline:article',
                link : '/admin/templates',
                meta : { platform: APP_PLATFORM_TYPES.Web },
            },
            {
                id   : 'admin.settings',
                title: 'Settings',
                type : 'basic',
                icon : 'mat_outline:settings',
                link : '/admin/settings',
                meta : { platform: APP_PLATFORM_TYPES.Web },
            },
        ],
    },

    // ─── Profile ─────────────────────────────────────────────────────────────
    // Hidden for MVP — same decision as the web user-menu "Profile" item
    // (docs/backlog/MODULE-2-V2-BACKLOG.md BL2-04): there's no real profile
    // page yet, only notification preferences, and system_admin can already
    // correct a user's own record via the Users admin screen if needed.
    // Hidden, not deleted — uncomment when the V2 self-service profile page
    // ships (BL2-04's "When this ships" section).
    /*
    {
        id   : 'profile',
        title: 'Profile',
        type : 'basic',
        icon : 'heroicons_outline:user-circle',
        link : '/profile/notifications',
        meta : {
            platform: APP_PLATFORM_TYPES.Mobile,
            roles   : ['field_staff', 'office_staff', 'system_admin'],
        },
    },
    */

    // ─── Platform ────────────────────────────────────────────────────────────
    // Tenant management. super_admin only.
    {
        id      : 'platform',
        title   : 'Platform',
        subtitle: 'Manage tenants and platform settings',
        type    : 'group',
        meta    : {
            platform   : APP_PLATFORM_TYPES.Web,
            roles      : ['super_admin'],
            hiddenInApp: true,
        },
        children: [
            {
                id   : 'platform.tenants',
                title: 'Tenants',
                type : 'basic',
                icon : 'mat_outline:domain',
                link : '/platform-admin/tenants/list',
                meta : {
                    platform: APP_PLATFORM_TYPES.Web,
                    roles   : ['super_admin'],
                },
            },
            {
                id   : 'platform.compliance',
                title: 'Compliance Alerts',
                type : 'basic',
                icon : 'heroicons_outline:shield-exclamation',
                link : '/platform-admin/compliance',
                meta : {
                    platform: APP_PLATFORM_TYPES.Web,
                    roles   : ['super_admin'],
                },
            },
            {
                id   : 'platform.templates',
                title: 'Platform templates',
                type : 'basic',
                icon : 'mat_outline:article',
                link : '/platform-admin/templates',
                meta : {
                    platform: APP_PLATFORM_TYPES.Web,
                    roles   : ['super_admin'],
                },
            },
            {
                id   : 'platform.billing',
                title: 'Billing',
                type : 'basic',
                icon : 'mat_outline:receipt_long',
                link : '/platform-admin/invoicing',
                meta : {
                    platform: APP_PLATFORM_TYPES.Web,
                    roles   : ['super_admin'],
                },
            },
        ],
    },
];
